import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import {
  Link,
  createFileRoute,
  redirect,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/customers` — accountant/admin-only listing. Same shape as
 * `/users`: paginated table, search filter on name, admin-only
 * "Add Customer" button. Server gate (accountant+) is enforced
 * via 403 -> redirect-to-/my on the loader.
 */

const SORT_KEYS = ["name", "fakturoidSubjectId", "projects", "invoices", "createdAt"] as const;
type SortKey = (typeof SORT_KEYS)[number];

const customersSearchSchema = z.object({
  q: z.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
});

type CustomersSearch = z.infer<typeof customersSearchSchema>;

type ListedCustomer = {
  id: string;
  name: string;
  fakturoidSubjectId: string | null;
  createdAt: string;
  _count: { projects: number; invoiceBatches: number };
};

type CustomersResponse = {
  customers: ListedCustomer[];
  total: number;
  page: number;
  pageSize: number;
};

export const Route = createFileRoute("/_app/customers/")({
  validateSearch: customersSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.q) params.set("q", deps.q);
    if (deps.sort) params.set("sort", deps.sort);
    if (deps.dir) params.set("dir", deps.dir);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    const qs = params.toString();
    try {
      return await api.get<CustomersResponse>(qs ? `/api/customers?${qs}` : "/api/customers");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: CustomersPage,
});

function CustomersPage() {
  const { t } = useTranslation();
  const { customers, total, page, pageSize } = Route.useLoaderData();
  const search: CustomersSearch = Route.useSearch();
  const navigate = useNavigate();
  const layout = useLoaderData({ from: "/_app" });
  const admin = layout.user.globalRole === "admin";

  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "asc",
    onChange: (key, dir) =>
      navigate({
        to: "/customers",
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  const columns: Array<DataTableColumn<ListedCustomer>> = [
    {
      key: "name",
      header: t("customers.list.columns.name"),
      sortAccessor: (c) => c.name,
      cellClassName: "font-medium",
      cell: (c) => (
        <Link to="/customers/$id" params={{ id: c.id }} className="hover:underline text-primary">
          {c.name}
        </Link>
      ),
    },
    {
      key: "fakturoidSubjectId",
      header: t("customers.list.columns.fakturoidId"),
      sortAccessor: (c) => c.fakturoidSubjectId,
      cell: (c) =>
        c.fakturoidSubjectId ? (
          <Badge variant="outline">{c.fakturoidSubjectId}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      key: "projects",
      header: t("customers.list.columns.projects"),
      sortAccessor: (c) => c._count.projects,
      cell: (c) => c._count.projects,
    },
    {
      key: "invoices",
      header: t("customers.list.columns.invoices"),
      sortAccessor: (c) => c._count.invoiceBatches,
      cell: (c) => c._count.invoiceBatches,
    },
    {
      key: "createdAt",
      header: t("customers.list.columns.created"),
      sortAccessor: (c) => c.createdAt,
      cellClassName: "text-muted-foreground text-sm",
      cell: (c) => <LocalDate value={c.createdAt} />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("customers.list.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("customers.list.total", { count: total })}
          </p>
        </div>
        {admin && (
          <Button asChild>
            <Link to="/customers/new">{t("customers.list.add")}</Link>
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <SearchInput placeholder={t("customers.list.searchPlaceholder")} />
      </div>

      <DataTable
        columns={columns}
        rows={customers}
        rowKey={(c) => c.id}
        sort={sort}
        emptyState={t("customers.list.empty")}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="customers"
        basePath="/customers"
        searchParams={{
          q: search.q,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
